import { FC, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import type { RequirementThread } from './types';

interface DeleteThreadDialogProps {
  /** The thread to delete, or null when the dialog is closed. */
  target: RequirementThread | null;
  /** Performs the delete; resolves true on success. */
  onConfirm: (threadId: string) => Promise<boolean>;
  /** Called after a successful delete (parent closes + refreshes). */
  onDeleted: (thread: RequirementThread) => void;
  onCancel: () => void;
}

/**
 * S-05 delete confirmation. On failure the dialog stays open and
 * shows an inline error; on success the parent is notified to close + refresh.
 */
export const DeleteThreadDialog: FC<DeleteThreadDialogProps> = ({
  target,
  onConfirm,
  onDeleted,
  onCancel,
}) => {
  const { t } = useTranslation('requirement-lifecycle');
  const [deleting, setDeleting] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleConfirm = async () => {
    if (!target) return;
    setDeleting(true);
    setFailed(false);
    const ok = await onConfirm(target.threadId);
    setDeleting(false);
    if (ok) {
      onDeleted(target);
    } else {
      setFailed(true);
    }
  };

  const handleClose = () => {
    if (deleting) return;
    setFailed(false);
    onCancel();
  };

  const label = target?.name || (target ? `#${target.threadId}` : '');

  return (
    <Dialog
      open={target !== null}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('delete.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('delete.body')}</DialogContentText>
        <DialogContentText>
          <strong>
            {t('delete.target')} {label}
          </strong>
        </DialogContentText>
        {failed && <Alert severity="error">{t('delete.error')}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={deleting}>
          {t('common.buttons.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="secondary"
          variant="contained"
          disabled={deleting}
          endIcon={deleting ? <CircularProgress size={18} /> : undefined}
        >
          {t('common.buttons.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteThreadDialog;
